import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { InviteShell } from '../auth/AcceptInviteLayout';
import { refreshEmailVerified } from '../../services/auth';
import { SignupError, UNKNOWN_MESSAGE, startSignupCheckout } from '../../services/signup';
import type { SignupStatusResponse } from '../../types/signup';
import { SignupContactPath, SignupHeader } from './SignupLayout';
import { SignupSchoolSummary } from './SignupSchoolSummary';
import { redirectToCheckout } from './redirect';
import { formatPrice, sportLabel } from './signupFormat';
import { useEnterApp } from './useEnterApp';
import { SignOutButton } from './SignupSignOutButton';
import type { SignupCompleteState } from './SignupCompletePage';
import { useSignupStatus } from './useSignupStatus';

/**
 * Where an unpaid self-serve admin completes payment: the end of signup, Stripe Checkout's cancel
 * URL, and where `ProtectedRoute` sends a returning admin whose organization is still awaiting
 * payment. Everything shown is read from GET /api/signup/status; nothing is priced client side.
 */
export const SignupPaymentPage = () => {
  const { state, reload } = useSignupStatus();

  if (state.phase === 'loading') {
    return (
      <InviteShell>
        <div className="accept-invite-loading" role="status">
          <p>Loading your account...</p>
        </div>
      </InviteShell>
    );
  }

  if (state.phase === 'error') {
    return (
      <InviteShell>
        <SignupHeader title="We could not load your account" />
        <div className="accept-invite-message accept-invite-message--error">
          <p>{state.message}</p>
        </div>
        <div className="accept-invite-actions">
          <Button type="button" fullWidth onClick={() => void reload()} data-testid="signup-status-retry">
            Try again
          </Button>
          <SignOutButton />
        </div>
      </InviteShell>
    );
  }

  if (!state.status.awaitingPayment) {
    return <AlreadyActive />;
  }

  // Checked before `checkoutAvailable`, which is false while a payment clears: that is not
  // "online payment unavailable", it is "wait for the first payment". The completion page polls
  // until the webhook activates the account.
  if (state.status.paymentPending) {
    const pendingState: SignupCompleteState = { paymentPending: true };
    return <Navigate to="/signup/complete" replace state={pendingState} />;
  }

  return <AwaitingPayment status={state.status} onReload={reload} />;
};

/** Paid already (or never a self-serve org): straight into the app, with a fallback button. */
const AlreadyActive = () => {
  const { enterApp, entering, error } = useEnterApp();
  const startedRef = useRef(false);

  useEffect(() => {
    // Once, even under StrictMode's double effect.
    if (startedRef.current) return;
    startedRef.current = true;
    void enterApp();
  }, [enterApp]);

  return (
    <InviteShell>
      <SignupHeader title="Your account is active" />
      {error && (
        <div className="accept-invite-message accept-invite-message--error" role="alert">
          <p>{error}</p>
        </div>
      )}
      <div className="accept-invite-actions">
        <Button
          type="button"
          fullWidth
          loading={entering}
          onClick={() => void enterApp()}
          data-testid="signup-enter-app"
        >
          Go to RosterPilot
        </Button>
      </div>
    </InviteShell>
  );
};

const AwaitingPayment = ({
  status,
  onReload,
}: {
  status: SignupStatusResponse;
  onReload: () => Promise<void>;
}) => {
  const { enterApp, error: enterError } = useEnterApp();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  // The button stays loading while the browser leaves for Stripe. Back from Stripe can restore this
  // page from the back/forward cache with that state intact, leaving the button stuck; a restored
  // page (`persisted`) resets it.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setStarting(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const price =
    status.annualPriceCents !== null ? formatPrice(status.annualPriceCents, status.currency) : null;
  const sport = sportLabel(status.sport);

  const handleCheckout = async () => {
    setError('');
    setStarting(true);

    try {
      const { checkoutUrl } = await startSignupCheckout();
      redirectToCheckout(checkoutUrl);
      // Leave the button in its loading state: the browser is on its way to Stripe.
      return;
    } catch (checkoutError: unknown) {
      if (checkoutError instanceof SignupError) {
        if (checkoutError.code === 'not_awaiting_payment') {
          // Paid in the meantime (another tab, or the webhook beat us here). A failure to open the
          // app surfaces as `enterError` below, with the button live again for a retry.
          setStarting(false);
          await enterApp();
          return;
        }
        if (checkoutError.code === 'payment_pending') {
          // An earlier checkout's payment has not cleared yet. A second checkout could charge twice,
          // so wait on the first: the completion page polls until the webhook activates the account.
          const state: SignupCompleteState = { paymentPending: true };
          navigate('/signup/complete', { replace: true, state });
          return;
        }
        setError(
          checkoutError.code === 'payment_provider_error'
            ? 'We could not start checkout. Please try again in a minute.'
            : checkoutError.message
        );
      } else {
        setError(UNKNOWN_MESSAGE);
      }
    }
    setStarting(false);
  };

  return (
    <InviteShell>
      <SignupHeader
        title="Complete your payment"
        subtitle={status.organizationName}
        focusOnMount={false}
      />

      {status.school && (
        <SignupSchoolSummary
          school={status.school}
          annualPriceCents={status.annualPriceCents}
          currency={status.currency}
        />
      )}

      <div className="accept-invite-message accept-invite-message--warning">
        {sport && <p data-testid="signup-payment-sport">Sport: {sport}</p>}
        {price && (
          <p data-testid="signup-payment-price">
            Your program will be billed {price} per year. No free trial.
          </p>
        )}
        {status.message && <p>{status.message}</p>}
      </div>

      {(error || enterError) && (
        <div
          className="accept-invite-message accept-invite-message--error"
          role="alert"
          data-testid="signup-checkout-error"
        >
          <p>{error || enterError}</p>
        </div>
      )}

      {status.checkoutAvailable ? (
        <div className="accept-invite-actions">
          <Button
            type="button"
            fullWidth
            loading={starting}
            onClick={() => void handleCheckout()}
            data-testid="signup-checkout-button"
          >
            Continue to payment
          </Button>
          <SignOutButton />
        </div>
      ) : !status.emailVerified ? (
        <VerifyEmailFirst onReload={onReload} />
      ) : (
        <>
          <p className="signup-status" data-testid="signup-checkout-unavailable">
            Online payment isn&apos;t available right now.
          </p>
          <SignupContactPath>
            <SignOutButton />
          </SignupContactPath>
        </>
      )}
    </InviteShell>
  );
};

/**
 * Checkout is refused until the address is verified, which is not "online payment unavailable":
 * the coach can fix it. "I've verified" reloads the Firebase account and refreshes its ID token
 * (the API reads `email_verified` from the token) before re-reading the status.
 */
const VerifyEmailFirst = ({ onReload }: { onReload: () => Promise<void> }) => {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    setError('');
    setChecking(true);
    let verified = false;
    try {
      verified = await refreshEmailVerified();
    } catch {
      setError('We could not check your verification status. Please try again.');
      setChecking(false);
      return;
    }
    setChecking(false);
    if (!verified) {
      setError("Your email isn't verified yet. Open the link we sent you, then try again.");
      return;
    }
    await onReload();
  };

  return (
    <>
      <div
        className="accept-invite-message accept-invite-message--warning"
        data-testid="signup-payment-verify-email"
      >
        <p>Verify your email to continue to payment.</p>
        <p>Open the verification link we sent you, then choose I&apos;ve verified.</p>
      </div>
      {error && (
        <div className="accept-invite-message accept-invite-message--error" role="alert">
          <p>{error}</p>
        </div>
      )}
      <div className="accept-invite-actions">
        <Button
          type="button"
          fullWidth
          loading={checking}
          onClick={() => void handleCheck()}
          data-testid="signup-payment-verified-button"
        >
          I&apos;ve verified
        </Button>
        <SignOutButton />
      </div>
    </>
  );
};
